import type { LucideIcon, LucideProps } from 'lucide-react'

import { cn } from '@/libs/utils'

/**
 * Semantic color slots. Each maps to a design-system token that satisfies
 * WCAG AA contrast against its intended surface, so icons stay readable in
 * both light and dark mode.
 */
export type IconTone =
  | 'default' // foreground on background
  | 'muted' // muted-foreground on background (for de-emphasised glyphs)
  | 'primary' // brand primary
  | 'accent' // secondary brand accent
  | 'success'
  | 'warning'
  | 'destructive'
  | 'info'
  | 'inverse' // for use on primary/dark surfaces (e.g. inside a filled button)
  | 'sidebar' // for use on the sidebar surface

/**
 * Standardized icon sizes. Values come from the shared `--icon-size-*` scale
 * defined in `src/index.css`, so every icon stays on the same rhythm at
 * every breakpoint.
 *
 *  - `xs` 12px — dense inline glyphs (badges, chips)
 *  - `sm` 14px — table cells, small buttons
 *  - `md` 16px — default UI icon (nav, inline)
 *  - `lg` 20px — primary buttons, section headers
 *  - `xl` 24px — feature callouts, empty states
 *  - `xxl` 32px — hero / illustration slots
 *  - `responsive` — 16 → 20 → 24 across sm / lg
 */
export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'responsive'

type BaseProps = Omit<LucideProps, 'color' | 'size' | 'aria-label' | 'aria-hidden'>

type IconA11yProps =
  | {
      /** Decorative icon: hidden from assistive tech. Use when the surrounding
       * text already conveys the meaning (e.g. `<X /> Delete`). */
      decorative: true
      label?: never
    }
  | {
      /** Accessible name announced by screen readers. Required for standalone
       * icons (icon-only buttons, status indicators, etc.). */
      label: string
      decorative?: false
    }

export type IconProps = BaseProps &
  IconA11yProps & {
    /** Lucide icon component. */
    icon: LucideIcon
    /** Semantic color slot. Defaults to `default`. */
    tone?: IconTone
    /** Standardized size token, or a raw pixel value. Defaults to `md`. */
    size?: IconSize | number
  }

const toneClass: Record<IconTone, string> = {
  default: 'text-foreground',
  muted: 'text-muted-foreground',
  primary: 'text-primary',
  accent: 'text-accent-foreground',
  success: 'text-success',
  warning: 'text-warning',
  destructive: 'text-destructive',
  info: 'text-primary',
  inverse: 'text-primary-foreground',
  sidebar: 'text-sidebar-foreground',
}

const sizeClass: Record<IconSize, string> = {
  xs: 'icon-xs',
  sm: 'icon-sm',
  md: 'icon-md',
  lg: 'icon-lg',
  xl: 'icon-xl',
  xxl: 'icon-xxl',
  responsive: 'icon-responsive',
}

/**
 * `Icon` — the single canonical way to render an icon in the app.
 *
 * Guarantees:
 *  - **Consistent size** via the shared `--icon-size-*` scale.
 *  - **Consistent color** via design-system tokens that pass WCAG AA contrast
 *    in both light and dark mode (no arbitrary `text-gray-*` values).
 *  - **Consistent stroke** via `--icon-stroke-width`, which is bumped in dark
 *    mode so hairline strokes remain visible.
 *  - **Accessible by construction**: the `decorative` / `label` union forces
 *    every call site to declare intent — either hide the icon from AT or give
 *    it an accessible name. No silently unlabelled icons.
 *
 * Examples:
 * ```tsx
 * // Decorative icon next to text
 * <Icon icon={Trash2} decorative />
 *
 * // Icon-only status indicator
 * <Icon icon={CircleCheck} tone="success" label="Completed" />
 *
 * // Larger, responsive hero glyph
 * <Icon icon={Sparkles} tone="primary" size="responsive" decorative />
 * ```
 */
export function Icon({
  icon: LucideIconCmp,
  tone = 'default',
  size = 'md',
  className,
  decorative,
  label,
  ...rest
}: IconProps) {
  const resolvedSizeClass = typeof size === 'string' ? sizeClass[size] : undefined
  const pixelSize = typeof size === 'number' ? size : undefined

  const a11yProps = decorative
    ? { 'aria-hidden': true, focusable: false as const }
    : { role: 'img' as const, 'aria-label': label }

  return (
    <LucideIconCmp
      className={cn(
        'shrink-0',
        toneClass[tone],
        resolvedSizeClass,
        'stroke-[var(--icon-stroke-width,2)]',
        className,
      )}
      {...(pixelSize !== undefined ? { size: pixelSize } : {})}
      {...a11yProps}
      {...rest}
    />
  )
}
